import React, { useEffect } from 'react';

const DatabaseKeys = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Database Keys - Primary, Foreign, Composite Keys Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master database keys - Primary, Foreign, Composite, Unique, and Candidate keys. Learn normalization, referential integrity, and indexing strategies.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master database keys - Primary, Foreign, Composite, Unique, and Candidate keys. Learn normalization, referential integrity, and indexing strategies.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'database keys, primary key, foreign key, composite key, unique key, candidate key, referential integrity, normalization');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'database keys, primary key, foreign key, composite key, unique key, candidate key, referential integrity, normalization';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Database Keys</h1>
        <p style={styles.subtitle}>
          Understanding Primary, Foreign, Composite, and Other Key Types
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What are Database Keys?</h2>
          <p style={styles.paragraph}>
            Database keys are attributes or combinations of attributes that uniquely identify records 
            in a table and establish relationships between tables. They ensure data integrity, enable 
            efficient querying, and maintain referential integrity across the database.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>1. Primary Key</h2>
          <p style={styles.paragraph}>
            A primary key is a column or combination of columns that uniquely identifies each record 
            in a table. It cannot contain NULL values and must be unique across all records.
          </p>

          <div style={styles.codeBlock}>
{`-- Primary Key Examples

-- Single column primary key
CREATE TABLE users (
    user_id INT PRIMARY KEY,
    username VARCHAR(50) UNIQUE NOT NULL,
    email VARCHAR(100) UNIQUE NOT NULL,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

-- Auto-incrementing primary key
CREATE TABLE products (
    product_id INT AUTO_INCREMENT PRIMARY KEY,
    name VARCHAR(100) NOT NULL,
    price DECIMAL(10,2) NOT NULL,
    category_id INT
);

-- Composite primary key (multiple columns)
CREATE TABLE order_items (
    order_id INT,
    product_id INT,
    quantity INT NOT NULL,
    price DECIMAL(10,2) NOT NULL,
    PRIMARY KEY (order_id, product_id)
);

-- Adding primary key to existing table
ALTER TABLE customers 
ADD CONSTRAINT pk_customers PRIMARY KEY (customer_id);

-- Primary key with custom name
CREATE TABLE employees (
    emp_id INT,
    department_id INT,
    name VARCHAR(100) NOT NULL,
    CONSTRAINT pk_employee PRIMARY KEY (emp_id)
);`}
          </div>

          <h3 style={styles.heading}>Primary Key Characteristics</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Uniqueness:</strong> No two records can have the same primary key value</li>
            <li style={styles.listItem}><strong>Non-null:</strong> Primary key cannot contain NULL values</li>
            <li style={styles.listItem}><strong>Immutable:</strong> Primary key values should not change</li>
            <li style={styles.listItem}><strong>Minimal:</strong> Should contain the minimum number of columns necessary</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>2. Foreign Key</h2>
          <p style={styles.paragraph}>
            A foreign key is a column or combination of columns that creates a link between two tables. 
            It references the primary key of another table, establishing referential integrity.
          </p>

          <div style={styles.codeBlock}>
{`-- Foreign Key Examples

-- Create parent table first
CREATE TABLE categories (
    category_id INT PRIMARY KEY,
    name VARCHAR(50) NOT NULL,
    description TEXT
);

-- Create child table with foreign key
CREATE TABLE products (
    product_id INT PRIMARY KEY,
    name VARCHAR(100) NOT NULL,
    price DECIMAL(10,2) NOT NULL,
    category_id INT,
    FOREIGN KEY (category_id) REFERENCES categories(category_id)
);

-- Foreign key with custom name and actions
CREATE TABLE orders (
    order_id INT PRIMARY KEY,
    customer_id INT,
    order_date DATE NOT NULL,
    total_amount DECIMAL(10,2),
    CONSTRAINT fk_customer 
        FOREIGN KEY (customer_id) 
        REFERENCES customers(customer_id)
        ON DELETE CASCADE
        ON UPDATE CASCADE
);

-- Multiple foreign keys
CREATE TABLE order_items (
    order_item_id INT PRIMARY KEY,
    order_id INT,
    product_id INT,
    quantity INT NOT NULL,
    unit_price DECIMAL(10,2) NOT NULL,
    FOREIGN KEY (order_id) REFERENCES orders(order_id) ON DELETE CASCADE,
    FOREIGN KEY (product_id) REFERENCES products(product_id)
);

-- Self-referencing foreign key
CREATE TABLE employees (
    employee_id INT PRIMARY KEY,
    name VARCHAR(100) NOT NULL,
    manager_id INT,
    department VARCHAR(50),
    FOREIGN KEY (manager_id) REFERENCES employees(employee_id)
);

-- Adding foreign key to existing table
ALTER TABLE products
ADD CONSTRAINT fk_products_category
FOREIGN KEY (category_id) REFERENCES categories(category_id);`}
          </div>

          <h3 style={styles.heading}>Foreign Key Actions</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>CASCADE:</strong> Automatically update/delete child records when parent changes</li>
            <li style={styles.listItem}><strong>SET NULL:</strong> Set foreign key to NULL when parent is deleted</li>
            <li style={styles.listItem}><strong>SET DEFAULT:</strong> Set foreign key to default value when parent is deleted</li>
            <li style={styles.listItem}><strong>RESTRICT:</strong> Prevent deletion/update of parent if children exist</li>
            <li style={styles.listItem}><strong>NO ACTION:</strong> Similar to RESTRICT but checked at end of statement</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>3. Composite Key</h2>
          <p style={styles.paragraph}>
            A composite key is a primary key consisting of two or more columns. It's used when a 
            single column cannot uniquely identify a record, but a combination of columns can.
          </p>

          <div style={styles.codeBlock}>
{`-- Composite Key Examples

-- Order items: combination of order_id and product_id is unique
CREATE TABLE order_items (
    order_id INT,
    product_id INT,
    quantity INT NOT NULL,
    unit_price DECIMAL(10,2) NOT NULL,
    discount DECIMAL(5,2) DEFAULT 0,
    PRIMARY KEY (order_id, product_id),
    FOREIGN KEY (order_id) REFERENCES orders(order_id),
    FOREIGN KEY (product_id) REFERENCES products(product_id)
);

-- Student course enrollment
CREATE TABLE enrollments (
    student_id INT,
    course_id INT,
    semester VARCHAR(20),
    year INT,
    grade CHAR(2),
    enrollment_date DATE DEFAULT CURRENT_DATE,
    PRIMARY KEY (student_id, course_id, semester, year),
    FOREIGN KEY (student_id) REFERENCES students(student_id),
    FOREIGN KEY (course_id) REFERENCES courses(course_id)
);

-- Geographic location coordinates
CREATE TABLE locations (
    latitude DECIMAL(10,8),
    longitude DECIMAL(11,8),
    location_name VARCHAR(100) NOT NULL,
    country VARCHAR(50),
    description TEXT,
    PRIMARY KEY (latitude, longitude)
);

-- Time series data
CREATE TABLE sensor_readings (
    sensor_id INT,
    timestamp DATETIME,
    temperature DECIMAL(5,2),
    humidity DECIMAL(5,2),
    pressure DECIMAL(7,2),
    PRIMARY KEY (sensor_id, timestamp),
    FOREIGN KEY (sensor_id) REFERENCES sensors(sensor_id)
);

-- Composite foreign key referencing composite primary key
CREATE TABLE order_item_reviews (
    review_id INT AUTO_INCREMENT,
    order_id INT,
    product_id INT,
    customer_id INT,
    rating INT CHECK (rating BETWEEN 1 AND 5),
    review_text TEXT,
    review_date DATE DEFAULT CURRENT_DATE,
    PRIMARY KEY (review_id),
    FOREIGN KEY (order_id, product_id) REFERENCES order_items(order_id, product_id),
    FOREIGN KEY (customer_id) REFERENCES customers(customer_id)
);`}
          </div>

          <h3 style={styles.heading}>When to Use Composite Keys</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Many-to-many relationships:</strong> Junction tables often use composite keys</li>
            <li style={styles.listItem}><strong>Time series data:</strong> Entity + timestamp combinations</li>
            <li style={styles.listItem}><strong>Geographic data:</strong> Coordinate combinations</li>
            <li style={styles.listItem}><strong>Multi-dimensional data:</strong> When natural key requires multiple attributes</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>4. Unique Key</h2>
          <p style={styles.paragraph}>
            A unique key ensures that values in a column or combination of columns are unique across 
            all records. Unlike primary keys, unique keys can contain NULL values (but only one NULL per column).
          </p>

          <div style={styles.codeBlock}>
{`-- Unique Key Examples

-- Single column unique constraint
CREATE TABLE users (
    user_id INT PRIMARY KEY,
    username VARCHAR(50) UNIQUE NOT NULL,
    email VARCHAR(100) UNIQUE NOT NULL,
    phone VARCHAR(20) UNIQUE,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

-- Named unique constraint
CREATE TABLE products (
    product_id INT PRIMARY KEY,
    sku VARCHAR(50),
    name VARCHAR(100) NOT NULL,
    barcode VARCHAR(20),
    CONSTRAINT uk_product_sku UNIQUE (sku),
    CONSTRAINT uk_product_barcode UNIQUE (barcode)
);

-- Composite unique constraint
CREATE TABLE employees (
    employee_id INT PRIMARY KEY,
    employee_number VARCHAR(10),
    department_id INT,
    email VARCHAR(100),
    social_security_number VARCHAR(11),
    -- Employee number must be unique within department
    CONSTRAINT uk_emp_dept UNIQUE (employee_number, department_id),
    -- Email and SSN must be globally unique
    CONSTRAINT uk_emp_email UNIQUE (email),
    CONSTRAINT uk_emp_ssn UNIQUE (social_security_number)
);

-- Adding unique constraint to existing table
ALTER TABLE customers
ADD CONSTRAINT uk_customer_email UNIQUE (email);

-- Multiple unique constraints
CREATE TABLE vehicles (
    vehicle_id INT PRIMARY KEY,
    vin VARCHAR(17) UNIQUE NOT NULL,
    license_plate VARCHAR(10) UNIQUE,
    registration_number VARCHAR(20) UNIQUE,
    make VARCHAR(50) NOT NULL,
    model VARCHAR(50) NOT NULL,
    year INT NOT NULL
);

-- Partial unique index (database-specific)
-- PostgreSQL example: unique constraint on non-null values only
CREATE UNIQUE INDEX uk_products_sku_not_null 
ON products (sku) 
WHERE sku IS NOT NULL;`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>5. Candidate Key</h2>
          <p style={styles.paragraph}>
            A candidate key is a column or combination of columns that could serve as a primary key. 
            A table can have multiple candidate keys, but only one is chosen as the primary key.
          </p>

          <div style={styles.codeBlock}>
{`-- Candidate Key Examples

-- Multiple candidate keys in a user table
CREATE TABLE users (
    user_id INT,           -- Candidate key 1
    username VARCHAR(50),  -- Candidate key 2
    email VARCHAR(100),    -- Candidate key 3
    ssn VARCHAR(11),       -- Candidate key 4
    phone VARCHAR(20),
    first_name VARCHAR(50) NOT NULL,
    last_name VARCHAR(50) NOT NULL,
    
    -- Choose user_id as primary key
    PRIMARY KEY (user_id),
    
    -- Other candidate keys become unique constraints
    UNIQUE (username),
    UNIQUE (email),
    UNIQUE (ssn)
);

-- Product table with multiple candidate keys
CREATE TABLE products (
    product_id INT,        -- Candidate key 1 (chosen as PK)
    sku VARCHAR(50),       -- Candidate key 2
    upc VARCHAR(12),       -- Candidate key 3
    isbn VARCHAR(13),      -- Candidate key 4 (for books)
    name VARCHAR(100) NOT NULL,
    category VARCHAR(50),
    
    PRIMARY KEY (product_id),
    UNIQUE (sku),
    UNIQUE (upc),
    UNIQUE (isbn)
);

-- Employee table with composite candidate keys
CREATE TABLE employees (
    employee_id INT,                    -- Candidate key 1
    employee_number VARCHAR(10),        -- Part of candidate key 2
    department_id INT,                  -- Part of candidate key 2
    email VARCHAR(100),                 -- Candidate key 3
    badge_number VARCHAR(10),           -- Candidate key 4
    
    name VARCHAR(100) NOT NULL,
    hire_date DATE NOT NULL,
    
    PRIMARY KEY (employee_id),
    UNIQUE (employee_number, department_id),  -- Composite candidate key
    UNIQUE (email),
    UNIQUE (badge_number)
);`}
          </div>

          <h3 style={styles.heading}>Choosing the Right Primary Key</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Stability:</strong> Choose keys that rarely change</li>
            <li style={styles.listItem}><strong>Simplicity:</strong> Prefer single-column over composite keys</li>
            <li style={styles.listItem}><strong>Performance:</strong> Consider index size and query patterns</li>
            <li style={styles.listItem}><strong>Meaningfulness:</strong> Natural keys vs. surrogate keys trade-offs</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>6. Surrogate vs Natural Keys</h2>
          
          <div style={styles.codeBlock}>
{`-- Natural Key Example (meaningful data)
CREATE TABLE countries (
    country_code CHAR(2) PRIMARY KEY,  -- Natural key (ISO country code)
    country_name VARCHAR(100) NOT NULL,
    continent VARCHAR(50),
    population BIGINT
);

-- Surrogate Key Example (artificial identifier)
CREATE TABLE customers (
    customer_id INT AUTO_INCREMENT PRIMARY KEY,  -- Surrogate key
    customer_number VARCHAR(20) UNIQUE,          -- Natural key (business identifier)
    company_name VARCHAR(100) NOT NULL,
    contact_email VARCHAR(100) UNIQUE,
    phone VARCHAR(20),
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

-- Hybrid Approach: Surrogate PK + Natural Unique Key
CREATE TABLE orders (
    order_id BIGINT AUTO_INCREMENT PRIMARY KEY,  -- Surrogate key for performance
    order_number VARCHAR(20) UNIQUE NOT NULL,    -- Natural key for business logic
    customer_id INT NOT NULL,
    order_date DATE NOT NULL,
    status ENUM('pending', 'processing', 'shipped', 'delivered', 'cancelled'),
    
    FOREIGN KEY (customer_id) REFERENCES customers(customer_id),
    INDEX idx_order_number (order_number),
    INDEX idx_order_date (order_date)
);

-- Natural Composite Key Example
CREATE TABLE student_courses (
    student_id VARCHAR(10),     -- Natural identifier
    course_code VARCHAR(10),    -- Natural identifier
    semester VARCHAR(6),        -- Natural identifier (e.g., '2024S1')
    
    enrollment_date DATE NOT NULL,
    grade DECIMAL(3,2),
    credits INT NOT NULL,
    
    PRIMARY KEY (student_id, course_code, semester),
    FOREIGN KEY (student_id) REFERENCES students(student_id),
    FOREIGN KEY (course_code) REFERENCES courses(course_code)
);`}
          </div>

          <h3 style={styles.heading}>Natural vs Surrogate Key Comparison</h3>
          <div style={styles.codeBlock}>
{`| Aspect          | Natural Keys              | Surrogate Keys           |
|-----------------|---------------------------|--------------------------|
| Meaning         | Business meaningful       | No business meaning      |
| Stability       | May change over time      | Never change             |
| Performance     | Variable (depends on type)| Usually better (integers)|
| Storage         | Can be larger             | Usually smaller          |
| Relationships   | Meaningful in joins       | Requires lookup          |
| Uniqueness      | Naturally unique          | Artificially unique      |
| Examples        | SSN, Email, ISBN          | Auto-increment IDs       |`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Design Best Practices</h2>
          
          <h3 style={styles.heading}>1. Primary Key Guidelines</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Keep primary keys simple and stable</li>
            <li style={styles.listItem}>Use auto-incrementing integers for surrogate keys</li>
            <li style={styles.listItem}>Avoid using business data as primary keys unless very stable</li>
            <li style={styles.listItem}>Consider using UUIDs for distributed systems</li>
          </ul>

          <h3 style={styles.heading}>2. Foreign Key Guidelines</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Always define foreign key constraints for referential integrity</li>
            <li style={styles.listItem}>Choose appropriate ON DELETE and ON UPDATE actions</li>
            <li style={styles.listItem}>Index foreign key columns for better join performance</li>
            <li style={styles.listItem}>Consider the impact of cascading operations</li>
          </ul>

          <h3 style={styles.heading}>3. Indexing Strategy</h3>
          <div style={styles.codeBlock}>
{`-- Indexing examples for different key types

-- Primary keys automatically get unique indexes
CREATE TABLE users (
    user_id INT AUTO_INCREMENT PRIMARY KEY  -- Automatic unique index
);

-- Foreign keys should be indexed for join performance
CREATE TABLE orders (
    order_id INT PRIMARY KEY,
    customer_id INT,
    FOREIGN KEY (customer_id) REFERENCES customers(customer_id),
    INDEX idx_orders_customer (customer_id)  -- Index for FK
);

-- Composite key indexing
CREATE TABLE order_items (
    order_id INT,
    product_id INT,
    quantity INT,
    PRIMARY KEY (order_id, product_id),      -- Composite index on both columns
    INDEX idx_product_orders (product_id)    -- Additional index for product lookups
);

-- Unique constraint indexing
CREATE TABLE products (
    product_id INT PRIMARY KEY,
    sku VARCHAR(50) UNIQUE,                  -- Automatic unique index
    name VARCHAR(100),
    INDEX idx_product_name (name)            -- Additional index for searches
);`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Common Key Design Patterns</h2>
          
          <div style={styles.codeBlock}>
{`-- Pattern 1: User-Generated Content
CREATE TABLE blog_posts (
    post_id BIGINT AUTO_INCREMENT PRIMARY KEY,
    user_id INT NOT NULL,
    title VARCHAR(200) NOT NULL,
    slug VARCHAR(200) UNIQUE,              -- SEO-friendly URL
    content TEXT,
    published_at TIMESTAMP,
    
    FOREIGN KEY (user_id) REFERENCES users(user_id),
    INDEX idx_posts_user (user_id),
    INDEX idx_posts_published (published_at)
);

-- Pattern 2: Hierarchical Data
CREATE TABLE categories (
    category_id INT AUTO_INCREMENT PRIMARY KEY,
    parent_id INT,
    name VARCHAR(100) NOT NULL,
    path VARCHAR(500),                     -- Materialized path
    level INT DEFAULT 0,
    
    FOREIGN KEY (parent_id) REFERENCES categories(category_id),
    INDEX idx_categories_parent (parent_id),
    INDEX idx_categories_path (path)
);

-- Pattern 3: Audit Trail
CREATE TABLE audit_log (
    log_id BIGINT AUTO_INCREMENT PRIMARY KEY,
    table_name VARCHAR(50) NOT NULL,
    record_id VARCHAR(50) NOT NULL,       -- Can reference any table
    action ENUM('INSERT', 'UPDATE', 'DELETE') NOT NULL,
    old_values JSON,
    new_values JSON,
    user_id INT,
    timestamp TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    
    INDEX idx_audit_table_record (table_name, record_id),
    INDEX idx_audit_user (user_id),
    INDEX idx_audit_timestamp (timestamp)
);

-- Pattern 4: Multi-tenant Application
CREATE TABLE tenant_users (
    user_id BIGINT AUTO_INCREMENT,
    tenant_id INT NOT NULL,
    email VARCHAR(100) NOT NULL,
    username VARCHAR(50) NOT NULL,
    
    PRIMARY KEY (user_id),
    UNIQUE KEY uk_tenant_email (tenant_id, email),     -- Email unique per tenant
    UNIQUE KEY uk_tenant_username (tenant_id, username), -- Username unique per tenant
    
    FOREIGN KEY (tenant_id) REFERENCES tenants(tenant_id)
);`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Constraints and Validation</h2>
          
          <div style={styles.codeBlock}>
{`-- Advanced key constraints

-- Check constraints with keys
CREATE TABLE products (
    product_id INT AUTO_INCREMENT PRIMARY KEY,
    sku VARCHAR(50) UNIQUE NOT NULL,
    price DECIMAL(10,2) NOT NULL,
    discount_price DECIMAL(10,2),
    
    -- Ensure discount price is less than regular price
    CONSTRAINT chk_discount_price 
        CHECK (discount_price IS NULL OR discount_price < price),
    
    -- Ensure SKU follows pattern
    CONSTRAINT chk_sku_format 
        CHECK (sku REGEXP '^[A-Z]{2}[0-9]{4}$')
);

-- Conditional unique constraints (PostgreSQL example)
CREATE TABLE employees (
    employee_id INT PRIMARY KEY,
    email VARCHAR(100),
    is_active BOOLEAN DEFAULT true,
    
    -- Email must be unique among active employees only
    CONSTRAINT uk_active_employee_email 
        UNIQUE (email) WHERE is_active = true
);

-- Deferred constraint checking
CREATE TABLE orders (
    order_id INT PRIMARY KEY,
    customer_id INT,
    order_total DECIMAL(10,2),
    
    CONSTRAINT fk_orders_customer 
        FOREIGN KEY (customer_id) REFERENCES customers(customer_id)
        DEFERRABLE INITIALLY DEFERRED  -- Check at transaction end
);`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            Database keys are fundamental to relational database design. Understanding the different 
            types of keys and their appropriate usage is crucial for creating efficient, maintainable, 
            and scalable database schemas. Choose your key strategy based on your specific requirements 
            for performance, data integrity, and business logic.
          </p>
        </section>
      </main>
    </article>
  );
};

export default DatabaseKeys;
